import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { liteClient } from 'algoliasearch/lite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilter, 
  faSearch, 
  faTimes, 
  faCalendarAlt, 
  faMapMarkerAlt, 
  faUser, 
  faHistory,
  faChevronDown,
  faCheck,
  faExclamationTriangle,
  faMusic,
  faLock,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import { format, parse, differenceInHours, differenceInMonths } from 'date-fns';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  doc, 
  orderBy,
  limit,
  startAfter,
  setDoc,
  serverTimestamp,
  updateDoc,
  increment
} from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { Country, State, City } from 'country-state-city';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import styles from './Search.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import AuthCheckWrapper from '../AuthCheckWrapper';

// Initialize Algolia client
const client = liteClient('6DJNAZSX4P', '342932cbe26f7751ec87fa2b1ff32e4c');

// Cache key for recently viewed users
const RECENT_USERS_CACHE_KEY = 'recentlyViewedUsers';
// Cache key for lastSearchReset
const LAST_RESET_CACHE_KEY = 'lastSearchReset_';

// UserType options with Band added
const userTypeOptions = [
  { label: "Artist", value: "Artist" },
  { label: "Crew", value: "Crew" },
  { label: "Manager", value: "Manager" },
  { label: "Vendor", value: "Vendor" },
  { label: "Band", value: "Band" }, // New option
  { label: "Organization", value: "Organization" }
];

// Search limit constants by membership tier
const SEARCH_LIMITS = {
  'Free': 50,
  'Basic': 1000,
  'Pro': 2000,
  'Elite': 3000,
};

const userSubTypeOptions = {
  Music: {
    Artist: [
      
      { label: "Bass Guitarist", value: "Bass Guitarist" },
      { label: "Cellist", value: "Cellist" },
      { label: "Comedy Writer", value: "Comedy Writer" },
      { label: "DJ", value: "DJ" },
      { label: "Dhol Player", value: "Dhol Player" },
      { label: "Dholak Player", value: "Dholak Player" },
      { label: "Drummer", value: "Drummer" },
      { label: "Flutist", value: "Flutist" },
      { label: "Ghatam Player", value: "Ghatam Player" },
      { label: "Guitarist", value: "Guitarist" },
      { label: "Harmonium Player", value: "Harmonium Player" },
      { label: "Improv Comedian", value: "Improv Comedian" },
      { label: "Keyboardist", value: "Keyboardist" },
      { label: "Mridangam Player", value: "Mridangam Player" },
      { label: "Music Producer", value: "Music Producer" },
      { label: "Percussionist", value: "Percussionist" },
      { label: "Rapper", value: "Rapper" },
      { label: "Santoor Player", value: "Santoor Player" },
      { label: "Sarod Player", value: "Sarod Player" },
      { label: "Saxophonist", value: "Saxophonist" },
      { label: "Shehnai Player", value: "Shehnai Player" },
      { label: "Sitar Player", value: "Sitar Player" },
      { label: "Sketch Artist", value: "Sketch Artist" },
      { label: "Stand-Up Comedian", value: "Stand-Up Comedian" },
      { label: "Tabla Player", value: "Tabla Player" },
      { label: "Trumpeter", value: "Trumpeter" },
      { label: "Veena Player", value: "Veena Player" },
      { label: "Violinist", value: "Violinist" },
      { label: "Vocalist", value: "Vocalist" },
    ],
    Crew: [
      { label: "Backline Technician", value: "Backline Technician" },
      { label: "Dresser", value: "Dresser" },
      { label: "Drum Tech", value: "Drum Tech" },
      { label: "Guitar Tech", value: "Guitar Tech" },
      { label: "Keyboard Tech", value: "Keyboard Tech" },
      { label: "Light Engineer", value: "Light Engineer" },
      { label: "Lighting Technician", value: "Lighting Technician" },
      { label: "Makeup Artist", value: "Makeup Artist" },
      { label: "Merchandiser", value: "Merchandiser" },
      { label: "Monitor Engineer", value: "Monitor Engineer" },
      { label: "Photographer", value: "Photographer" },
      { label: "Rigger", value: "Rigger" },
      { label: "Road Manager", value: "Road Manager" },
      { label: "Security Personnel", value: "Security Personnel" },
      { label: "Sound Engineer", value: "Sound Engineer" },
      { label: "Stage Manager", value: "Stage Manager" },
      { label: "Stage Tech", value: "Stage Tech" },
      { label: "Tour Bus Driver", value: "Tour Bus Driver" },
      { label: "Videographer", value: "Videographer" },
    ],
    Manager: [
      { label: "Tour Manager", value: "Tour Manager" },
      { label: "Artist Manager", value: "Artist Manager" },
    ],
    Vendor: [
      { label: "Backline Provider", value: "Backline Provider" },
      { label: "Light Vendor", value: "Light Vendor" },
      { label: "Sound Vendor", value: "Sound Vendor" },
      { label: "Stage and Production Vendor", value: "Stage and Production Vendor" },
    ],
  
    Staff: [
    // Backline Staff
    { label: "Backline Equipment Coordinator", value: "Backline Equipment Coordinator" },
    { label: "Instrument Maintenance Technician", value: "Instrument Maintenance Technician" },
    { label: "Backline Logistics Coordinator", value: "Backline Logistics Coordinator" },
    { label: "Equipment Setup Technician", value: "Equipment Setup Technician" },
  
    // Lighting Staff
    { label: "Lighting Setup Technician", value: "Lighting Setup Technician" },
    { label: "Lighting Board Operator", value: "Lighting Board Operator" },
    { label: "Lighting Rig Technician", value: "Lighting Rig Technician" },
    { label: "DMX Systems Specialist", value: "DMX Systems Specialist" },
    { label: "Moving Light Technician", value: "Moving Light Technician" },
  
    // Sound Staff
    { label: "PA System Technician", value: "PA System Technician" },
    { label: "Audio Setup Specialist", value: "Audio Setup Specialist" },
    { label: "Speaker Systems Technician", value: "Speaker Systems Technician" },
    { label: "Audio Equipment Coordinator", value: "Audio Equipment Coordinator" },
    { label: "FOH Systems Technician", value: "FOH Systems Technician" },
  
    // Stage and Production Staff
    { label: "Stage Construction Technician", value: "Stage Construction Technician" },
    { label: "Truss System Specialist", value: "Truss System Specialist" },
    { label: "Stage Equipment Coordinator", value: "Stage Equipment Coordinator" },
    { label: "Production Setup Coordinator", value: "Production Setup Coordinator" },
    { label: "Staging Logistics Specialist", value: "Staging Logistics Specialist" }
  ],
  Organization: [
    { label: "Artist Management Company", value: "Artist Management Company" },
  ],
  },
  };
  
  
// Staff subtypes
const staffSubTypes = [
    // Backline Staff
    { label: "Backline Equipment Coordinator", value: "Backline Equipment Coordinator" },
    { label: "Instrument Maintenance Technician", value: "Instrument Maintenance Technician" },
    { label: "Backline Logistics Coordinator", value: "Backline Logistics Coordinator" },
    { label: "Equipment Setup Technician", value: "Equipment Setup Technician" },
  
    // Lighting Staff
    { label: "Lighting Setup Technician", value: "Lighting Setup Technician" },
    { label: "Lighting Board Operator", value: "Lighting Board Operator" },
    { label: "Lighting Rig Technician", value: "Lighting Rig Technician" },
    { label: "DMX Systems Specialist", value: "DMX Systems Specialist" },
    { label: "Moving Light Technician", value: "Moving Light Technician" },
  
    // Sound Staff
    { label: "PA System Technician", value: "PA System Technician" },
    { label: "Audio Setup Specialist", value: "Audio Setup Specialist" },
    { label: "Speaker Systems Technician", value: "Speaker Systems Technician" },
    { label: "Audio Equipment Coordinator", value: "Audio Equipment Coordinator" },
    { label: "FOH Systems Technician", value: "FOH Systems Technician" },
  
    // Stage and Production Staff
    { label: "Stage Construction Technician", value: "Stage Construction Technician" },
    { label: "Truss System Specialist", value: "Truss System Specialist" },
    { label: "Stage Equipment Coordinator", value: "Stage Equipment Coordinator" },
    { label: "Production Setup Coordinator", value: "Production Setup Coordinator" },
    { label: "Staging Logistics Specialist", value: "Staging Logistics Specialist" }
];

// Band genres (used as subtypes for bands)
const bandGenres = [
  { label: "Rock", value: "Rock" },
  { label: "Pop", value: "Pop" },
  { label: "Hip Hop", value: "Hip Hop" },
  { label: "Jazz", value: "Jazz" },
  { label: "Classical", value: "Classical" },
  { label: "Electronic", value: "Electronic" },
  { label: "R&B", value: "R&B" },
  { label: "Country", value: "Country" },
  { label: "Folk", value: "Folk" },
  { label: "Metal", value: "Metal" },
  { label: "Blues", value: "Blues" },
  { label: "Reggae", value: "Reggae" },
  { label: "Punk", value: "Punk" },
  { label: "Funk", value: "Funk" },
  { label: "Soul", value: "Soul" },
  { label: "Disco", value: "Disco" },
  { label: "Techno", value: "Techno" },
  { label: "House", value: "House" },
  { label: "Ambient", value: "Ambient" },
  { label: "Gospel", value: "Gospel" },
  { label: "Latin", value: "Latin" },
  { label: "World", value: "World" }
];

// Function to get cached lastSearchReset value
const getCachedLastReset = (userId) => {
  const cacheKey = `${LAST_RESET_CACHE_KEY}${userId}`;
  const cachedValue = localStorage.getItem(cacheKey);
  
  if (cachedValue) {
    try {
      const parsed = JSON.parse(cachedValue);
      return {
        timestamp: new Date(parsed.timestamp),
        searchCount: parsed.searchCount || 0,
        discoveryCount: parsed.discoveryCount || 0
      };
    } catch (error) {
      console.error('Error parsing cached lastSearchReset:', error);
      localStorage.removeItem(cacheKey);
    }
  }
  
  return null;
};

// Function to update cached lastSearchReset value
const updateCachedLastReset = (userId, timestamp, searchCount, discoveryCount) => {
  const cacheKey = `${LAST_RESET_CACHE_KEY}${userId}`;
  localStorage.setItem(cacheKey, JSON.stringify({
    timestamp: timestamp.toISOString(),
    cachedAt: new Date().toISOString(),
    searchCount,
    discoveryCount
  }));
};

// Generate a cache key for availableUsersByDate collection
const generateCacheDocId = (filters, date) => {
  const { city = '', state = '', country = '', userType = '', userSubType = '' } = filters;
  
  // Create a clean version of each parameter (no spaces, all lowercase)
  const cleanCity = (city || '').replace(/\s+/g, '_').toLowerCase();
  const cleanState = (state || '').replace(/\s+/g, '_').toLowerCase();
  const cleanCountry = (country || '').replace(/\s+/g, '_').toLowerCase();
  const cleanUserType = (userType || '').replace(/\s+/g, '_').toLowerCase();
  
  // For userSubType, handle arrays (multiple subtypes)
  let cleanUserSubType = '';
  if (Array.isArray(userSubType)) {
    cleanUserSubType = userSubType
      .map(sub => (sub || '').replace(/\s+/g, '_').toLowerCase())
      .sort()
      .join('_');
  } else {
    cleanUserSubType = (userSubType || '').replace(/\s+/g, '_').toLowerCase();
  }
  
  // Format the date consistently
  const formattedDate = date || '';
  
  // Combine all parts to create the document ID
  return `${cleanCity}_${cleanState}_${cleanCountry}_${cleanUserType}_${cleanUserSubType}_${formattedDate}`;
};

const Search = () => {
  const navigate = useNavigate();
  const { userData: currentUser, getProfileData } = useContext(UserContext);
  const { clearCache } = useContext(TourContext);
  
  // Tabs state
  const [activeMainTab, setActiveMainTab] = useState('search'); // 'search' or 'discover'
  const [activeSearchTab, setActiveSearchTab] = useState('All'); // Default to All tab initially
  
  // Form states
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [tempFilters, setTempFilters] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Location states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  
  // SubType states
  const [subTypeSearchTerm, setSubTypeSearchTerm] = useState('');
  const [allSubTypes, setAllSubTypes] = useState([]);
  const [filteredSubTypes, setFilteredSubTypes] = useState([]);
  const [selectedSubTypes, setSelectedSubTypes] = useState([]);
  const [isSubTypeDropdownOpen, setIsSubTypeDropdownOpen] = useState(false);
  
  // Discovery states
  const [selectedUserType, setSelectedUserType] = useState('Artist');
  const [discoveryDate, setDiscoveryDate] = useState('');
  const [formattedDiscoveryDate, setFormattedDiscoveryDate] = useState(''); // For display
  const [discoveryResults, setDiscoveryResults] = useState([]);
  const [hasMoreDiscoveryResults, setHasMoreDiscoveryResults] = useState(false);
  const [discoveryPage, setDiscoveryPage] = useState(1);
  const [hasDiscovered, setHasDiscovered] = useState(false);
  const [lastScannedUserId, setLastScannedUserId] = useState(null);
  const [allUsersQueried, setAllUsersQueried] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Search filters state
  const [searchFilters, setSearchFilters] = useState({});
  const [searchTempFilters, setSearchTempFilters] = useState({});

  // Separate filter states for discover tab
  const [discoverFilters, setDiscoverFilters] = useState({});
  
  // Search and discovery limits state
  const [searchCount, setSearchCount] = useState(0);
  const [discoveryCount, setDiscoveryCount] = useState(0);
  const [searchLimit, setSearchLimit] = useState(0);
  const [discoveryLimit, setDiscoveryLimit] = useState(0);
  const [lastSearchReset, setLastSearchReset] = useState(null);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [limitModalType, setLimitModalType] = useState(''); // 'search' or 'discovery'
  const [hasCheckedResetThisSession, setHasCheckedResetThisSession] = useState(false);
  
  // Track previous search parameters
  const [lastSearchParams, setLastSearchParams] = useState({
    term: '',
    tab: '',
    filters: {}
  });

  const [lastDiscoveryParams, setLastDiscoveryParams] = useState({
    userType: '',
    subTypes: [],
    country: '',
    state: '',
    city: '',
    date: ''
  });
  
  // Recently viewed users state
  const [recentlyViewedUsers, setRecentlyViewedUsers] = useState([]);
  
  // Refs for dropdown click outside handling
  const subTypeDropdownRef = useRef(null);
  const discoverSubTypeDropdownRef = useRef(null);
  const [isDiscoverSubTypeDropdownOpen, setIsDiscoverSubTypeDropdownOpen] = useState(false);
  
  // Super optimized check and reset function that minimizes Firestore reads
  const checkAndResetLimitsOnMount = async () => {
    if (!currentUser?.userId) return false;
    
    console.log('Starting optimized mount check with caching');
    
    // Step 1: Try to get cached lastSearchReset value
    const cachedData = getCachedLastReset(currentUser.userId);
    
    // Step 2: Client-side month check with cached value
    if (cachedData) {
      console.log('Found cached lastSearchReset:', cachedData);
      const clientNow = new Date();
      const cachedLastReset = cachedData.timestamp;
      
      // If same month/year according to client and cached data, we're done!
      if (clientNow.getMonth() === cachedLastReset.getMonth() && 
          clientNow.getFullYear() === cachedLastReset.getFullYear()) {
        console.log('Cache indicates same month, no reset needed');
        
        // Update state with cached values for consistency
        // But only if state doesn't already have these values
        if (searchCount !== cachedData.searchCount) {
          setSearchCount(cachedData.searchCount);
        }
        if (discoveryCount !== cachedData.discoveryCount) {
          setDiscoveryCount(cachedData.discoveryCount);
        }
        if (!lastSearchReset || lastSearchReset.getTime() !== cachedLastReset.getTime()) {
          setLastSearchReset(cachedLastReset);
        }
        
        return false; // No reset needed
      }
      
      console.log('Client check indicates possible month change, verifying with server');
    } else {
      console.log('No cached lastSearchReset found, will initialize');
    }
    
    // Step 3: Verify with server time (only if needed)
    try {
      const response = await fetch('/api/getServerTime', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get server time');
      }
      
      const data = await response.json();
      const serverTime = new Date(data.now);
      console.log(`Server time: ${serverTime.toISOString()}`);
      
      // Step 4: Handle missing lastSearchReset (first time initialization)
      if (!cachedData) {
        console.log('Initializing lastSearchReset (first time)');
        
        // We need to read user document only if we don't have values in state
        let currentSearchCount = searchCount;
        let currentDiscoveryCount = discoveryCount;
        let needsFirestoreUpdate = false;
        
        // If we don't have values in state, read from Firestore
        if (currentSearchCount === undefined || currentDiscoveryCount === undefined || !lastSearchReset) {
          const db = getFirestoreInstance();
          const userRef = doc(db, 'users', currentUser.userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            currentSearchCount = userData.searchCount || 0;
            currentDiscoveryCount = userData.discoveryCount || 0;
            
            // If Firestore already has lastSearchReset, use it
            if (userData.lastSearchReset) {
              const firestoreLastReset = new Date(userData.lastSearchReset.toDate());
              setLastSearchReset(firestoreLastReset);
              
              // Cache the values
              updateCachedLastReset(
                currentUser.userId, 
                firestoreLastReset,
                currentSearchCount,
                currentDiscoveryCount
              );
              
              // Check if month is different from server time
              if (serverTime.getMonth() !== firestoreLastReset.getMonth() || 
                  serverTime.getFullYear() !== firestoreLastReset.getFullYear()) {
                // Need to reset
                needsFirestoreUpdate = true;
              } else {
                // No reset needed, just update state
                setSearchCount(currentSearchCount);
                setDiscoveryCount(currentDiscoveryCount);
                return false;
              }
            } else {
              // Firestore doesn't have lastSearchReset, initialize it
              needsFirestoreUpdate = true;
            }
          } else {
            // User document doesn't exist (shouldn't happen), initialize
            needsFirestoreUpdate = true;
          }
        } else {
          // We have values in state, but no cache - just initialize the cache
          updateCachedLastReset(
            currentUser.userId, 
            serverTime,
            currentSearchCount,
            currentDiscoveryCount
          );
          return false;
        }
        
        // If we need to update Firestore
        if (needsFirestoreUpdate) {
          const db = getFirestoreInstance();
          const userRef = doc(db, 'users', currentUser.userId);
          
          // Reset counters or initialize in Firestore
          await updateDoc(userRef, {
            searchCount: 0,
            discoveryCount: 0,
            lastSearchReset: serverTimestamp()
          });
          
          // Update state
          setSearchCount(0);
          setDiscoveryCount(0);
          setLastSearchReset(serverTime);
          
          // Update cache
          updateCachedLastReset(currentUser.userId, serverTime, 0, 0);
          
          // Already checked this session
          setHasCheckedResetThisSession(true);
          
          return true; // Indicate that limits were reset
        }
      }
      
      // Step 5: Handle existing lastSearchReset (month change check)
      if (cachedData) {
        const cachedLastReset = cachedData.timestamp;
        
        // Check if server time indicates a different month
        if (serverTime.getMonth() !== cachedLastReset.getMonth() || 
            serverTime.getFullYear() !== cachedLastReset.getFullYear()) {
          console.log('Server confirms month change, resetting limits');
          
          // Reset in Firestore
          const db = getFirestoreInstance();
          const userRef = doc(db, 'users', currentUser.userId);
          
          await updateDoc(userRef, {
            searchCount: 0,
            discoveryCount: 0,
            lastSearchReset: serverTimestamp()
          });
          
          // Update state
          setSearchCount(0);
          setDiscoveryCount(0);
          setLastSearchReset(serverTime);
          
          // Update cache
          updateCachedLastReset(currentUser.userId, serverTime, 0, 0);
          
          // Already checked this session
          setHasCheckedResetThisSession(true);
          
          return true; // Indicate that limits were reset
        } else {
          console.log('Server confirms same month, no reset needed');
          
          // Update cache timestamp (refresh)
          updateCachedLastReset(
            currentUser.userId, 
            cachedLastReset,
            cachedData.searchCount,
            cachedData.discoveryCount
          );
          
          return false;
        }
      }
    } catch (error) {
      console.error('Error in optimized mount check:', error);
    }
    
    return false;
  };
  
  // Function to check and reset limits if a month has passed
  const checkAndResetLimits = async () => {
    if (!currentUser?.userId) return false;
    
    try {
      // Only check for reset if we haven't already checked in this session
      if (!hasCheckedResetThisSession) {
        // Try to use cached lastSearchReset for quick check
        const cachedData = getCachedLastReset(currentUser.userId);
        
        if (cachedData) {
          const clientNow = new Date();
          const cachedLastReset = cachedData.timestamp;
          
          // If same month/year according to client, skip server check
          if (clientNow.getMonth() === cachedLastReset.getMonth() && 
              clientNow.getFullYear() === cachedLastReset.getFullYear()) {
            
            setHasCheckedResetThisSession(true);
            return false; // No reset needed
          }
        }
        
        // Need to verify with server time
        const response = await fetch('/api/getServerTime', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to get server time');
        }
        
        const data = await response.json();
        const serverTime = new Date(data.now);
        
        // Check if it's a new month (either no lastSearchReset or different month)
        if (!lastSearchReset) {
          // Initialize if missing
          const db = getFirestoreInstance();
          const userRef = doc(db, 'users', currentUser.userId);
          
          await updateDoc(userRef, {
            searchCount: currentUser.searchCount || 0,
            discoveryCount: currentUser.discoveryCount || 0,
            lastSearchReset: serverTimestamp()
          });
          
          // Update state and cache
          setLastSearchReset(serverTime);
          updateCachedLastReset(
            currentUser.userId, 
            serverTime, 
            currentUser.searchCount || 0,
            currentUser.discoveryCount || 0
          );
          
          setHasCheckedResetThisSession(true);
          return false;
        } else {
          // Check for month change
          const serverTimeMonth = serverTime.getMonth();
          const serverTimeYear = serverTime.getFullYear();
          const lastResetMonth = lastSearchReset.getMonth();
          const lastResetYear = lastSearchReset.getFullYear();
          
          if (serverTimeMonth !== lastResetMonth || serverTimeYear !== lastResetYear) {
            // Reset counters in Firestore
            const db = getFirestoreInstance();
            const userRef = doc(db, 'users', currentUser.userId);
            
            await updateDoc(userRef, {
              searchCount: 0,
              discoveryCount: 0,
              lastSearchReset: serverTimestamp()
            });
            
            // Reset state and cache
            setSearchCount(0);
            setDiscoveryCount(0);
            setLastSearchReset(serverTime);
            updateCachedLastReset(currentUser.userId, serverTime, 0, 0);
            
            setHasCheckedResetThisSession(true);
            return true; // Indicate reset happened
          }
        }
        
        setHasCheckedResetThisSession(true);
      }
      
      return false; // No reset needed
    } catch (error) {
      console.error('Error checking or resetting limits:', error);
      return false;
    }
  };
  
  // Function to update just the search counts in cache
  const updateSearchCountsCache = (userId, searchCount, discoveryCount) => {
    const cacheKey = `${LAST_RESET_CACHE_KEY}${userId}`;
    const cachedValue = localStorage.getItem(cacheKey);
    
    if (cachedValue) {
      try {
        const parsed = JSON.parse(cachedValue);
        
        // Update the counts while preserving timestamp
        localStorage.setItem(cacheKey, JSON.stringify({
          ...parsed,
          searchCount,
          discoveryCount
        }));
      } catch (error) {
        console.error('Error updating cached search counts:', error);
      }
    }
  };
  
  // Function to increment search/discovery count
  const incrementSearchCount = async (type = 'search') => {
    if (!currentUser?.userId) return false;
    
    try {
      // First check if we need to reset monthly counts
      const wasReset = await checkAndResetLimits();
      
      // Get the current counts (which may have just been reset to 0)
      const currentSearchCount = wasReset ? 0 : searchCount;
      const currentDiscoveryCount = wasReset ? 0 : discoveryCount;
      
      // Check if user has exceeded their limit
      if (type === 'search' && currentSearchCount >= searchLimit) {
        setLimitModalType('search');
        setIsLimitModalOpen(true);
        return false;
      }
      
      if (type === 'discovery' && currentDiscoveryCount >= discoveryLimit) {
        setLimitModalType('discovery');
        setIsLimitModalOpen(true);
        return false;
      }
      
      // Increment counter in Firestore
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', currentUser.userId);
      
      if (type === 'search') {
        await updateDoc(userRef, {
          searchCount: increment(1)
        });
        
        // Update local state
        const newSearchCount = currentSearchCount + 1;
        setSearchCount(newSearchCount);
        
        // Update cache with new search count
        updateSearchCountsCache(currentUser.userId, newSearchCount, currentDiscoveryCount);
      } else {
        await updateDoc(userRef, {
          discoveryCount: increment(1)
        });
        
        // Update local state
        const newDiscoveryCount = currentDiscoveryCount + 1;
        setDiscoveryCount(newDiscoveryCount);
        
        // Update cache with new discovery count
        updateSearchCountsCache(currentUser.userId, currentSearchCount, newDiscoveryCount);
      }
      
      return true;
    } catch (error) {
      console.error(`Error incrementing ${type} count:`, error);
      return false;
    }
  };
  
  // Load all subtypes on component mount
  useEffect(() => {
    const subtypes = getAllSubTypes();
    setAllSubTypes(subtypes);
    setFilteredSubTypes(subtypes);
  }, []);
  
  // Update subtypes when user type changes in discovery
  useEffect(() => {
    if (activeMainTab === 'discover') {
      const subtypes = getAllSubTypes(selectedUserType);
      setAllSubTypes(subtypes);
      setFilteredSubTypes(subtypes);
      
      // Reset selected subtypes when user type changes
      setSelectedSubTypes([]);
    }
  }, [selectedUserType, activeMainTab]);
  
  // Filter subtypes based on search term
  useEffect(() => {
    if (subTypeSearchTerm.trim() === '') {
      setFilteredSubTypes(allSubTypes);
    } else {
      const filtered = allSubTypes.filter(subType => 
        subType.label.toLowerCase().includes(subTypeSearchTerm.toLowerCase())
      );
      setFilteredSubTypes(filtered);
    }
  }, [subTypeSearchTerm, allSubTypes]);
  
  // Set default location values based on user data
  useEffect(() => {
    if (currentUser) {
      // Set default country if available
      if (currentUser.countryCode) {
        const countryObj = Country.getCountryByCode(currentUser.countryCode);
        if (countryObj) {
          setSelectedCountry(countryObj);
          setTempFilters(prev => ({ ...prev, country: countryObj.name }));
          
          // Set available states for this country
          const states = State.getStatesOfCountry(currentUser.countryCode);
          setAvailableStates(states);
          
          // Set default state if available
          if (currentUser.stateCode) {
            const stateObj = State.getStateByCodeAndCountry(currentUser.stateCode, currentUser.countryCode);
            if (stateObj) {
              setSelectedState(stateObj);
              setTempFilters(prev => ({ ...prev, state: stateObj.name }));
              
              // Set available cities for this state
              const cities = City.getCitiesOfState(currentUser.countryCode, currentUser.stateCode);
              setAvailableCities(cities);
              
              // Set default city if available
              if (currentUser.cityName) {
                const cityObj = cities.find(city => city.name === currentUser.cityName);
                if (cityObj) {
                  setSelectedCity(cityObj);
                  setTempFilters(prev => ({ ...prev, city: cityObj.name }));
                }
              }
            }
          }
        }
      }
      
      // Set search limits based on membership tier
      const tier = currentUser.membershipTier || 'Free';
      setSearchLimit(SEARCH_LIMITS[tier] || 50);
      setDiscoveryLimit(tier === 'Free' ? 0 : SEARCH_LIMITS[tier] || 0);
      
      // Load values without immediately checking for reset
      setSearchCount(currentUser.searchCount || 0);
      setDiscoveryCount(currentUser.discoveryCount || 0);
      
      if (currentUser.lastSearchReset) {
        setLastSearchReset(new Date(currentUser.lastSearchReset));
      }
      
      // Check for reset with small delay (to allow state to update)
      setTimeout(() => {
        checkAndResetLimitsOnMount();
      }, 300);
    }
  }, [currentUser]);
  
  // Load recently viewed users from localStorage
  useEffect(() => {
    const cachedUsers = localStorage.getItem(RECENT_USERS_CACHE_KEY);
    if (cachedUsers) {
      try {
        const parsedUsers = JSON.parse(cachedUsers);
        setRecentlyViewedUsers(parsedUsers);
      } catch (error) {
        console.error('Error parsing cached users:', error);
        localStorage.removeItem(RECENT_USERS_CACHE_KEY);
      }
    }
  }, []);
  
  // Handle click outside of SubType dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Skip if clicking inside a dropdown option or search input
      if (event.target.closest('.subTypeOptionsList') || 
          event.target.closest('.subTypeSearchContainer')) {
        return;
      }
      
      // For dropdown in the filter modal
      if (subTypeDropdownRef.current && 
          !subTypeDropdownRef.current.contains(event.target)) {
        setIsSubTypeDropdownOpen(false);
      }
      
      // For dropdown in the discover tab
      if (discoverSubTypeDropdownRef.current && 
          !discoverSubTypeDropdownRef.current.contains(event.target)) {
        setIsDiscoverSubTypeDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Check if all required discovery filters are set and show/clear validation error
  useEffect(() => {
    if (activeMainTab === 'discover') {
      // Check if all required filters are set
      if (discoveryDate && selectedUserType && selectedSubTypes.length > 0 && 
          selectedCountry && selectedState && selectedCity) {
        // All required filters are set
        setValidationError(null);
      }
    }
  }, [activeMainTab, discoveryDate, selectedUserType, selectedSubTypes, selectedCountry, selectedState, selectedCity]);
  
  // Format discovery date for display
  useEffect(() => {
    if (discoveryDate) {
      try {
        const date = new Date(discoveryDate);
        setFormattedDiscoveryDate(format(date, 'dd/MM/yyyy'));
      } catch (error) {
        console.error('Error formatting date:', error);
        setFormattedDiscoveryDate(discoveryDate);
      }
    } else {
      setFormattedDiscoveryDate('');
    }
  }, [discoveryDate]);
  
  // Get available tabs based on user type - show all tabs to all users
  const getAvailableTabs = () => {
    // Always return all tabs for everyone
    return ['All', 'Artist', 'Crew', 'Manager', 'Vendor', 'Bands', 'Organization', 'Staff'];
  };
  
  // Helper function to get all subtypes as a flat array
  const getAllSubTypes = (userType = null) => {
    // For Band userType, return genres as subtypes
    if (userType === 'Band') {
      return bandGenres;
    }
    
    // For Staff userType, return staff subtypes
    if (userType === 'Staff') {
      return staffSubTypes;
    }
    
    // For other user types, use the original implementation
    const allSubTypes = [];
    
    Object.values(userSubTypeOptions).forEach(industry => {
      Object.entries(industry).forEach(([type, subtypes]) => {
        if (!userType || type === userType) {
          subtypes.forEach(subType => {
            allSubTypes.push(subType);
          });
        }
      });
    });
    
    return allSubTypes;
  };

  // Handle standard search
  const handleSearch = async () => {
    // Check if search term is empty and there are no filters
    if ((!searchTerm.trim() && Object.keys(searchFilters).length === 0) || 
        (currentUser?.membershipTier === 'Free' && searchCount >= SEARCH_LIMITS.Free)) {
      return;
    }
    
    // Create current search parameters object for comparison
    const currentSearchParams = {
      term: searchTerm,
      tab: activeSearchTab,
      filters: { ...searchFilters }
    };
    
    // Check if this is the same search as last time
    const isSameSearch = 
      currentSearchParams.term === lastSearchParams.term &&
      currentSearchParams.tab === lastSearchParams.tab &&
      JSON.stringify(currentSearchParams.filters) === JSON.stringify(lastSearchParams.filters);
    
    // If it's the same search, don't increment the count
    let canProceed = true;
    if (!isSameSearch) {
      canProceed = await incrementSearchCount('search');
      if (!canProceed) return;
      
      // Update last search params after successful increment
      setLastSearchParams(currentSearchParams);
    }
    
    setIsLoading(true);
    try {
      // Handle different tabs
      if (activeSearchTab === 'All') {
        // Search across users and bands
        await searchAllCollections();
      } else if (activeSearchTab === 'Bands') {
        // Search only bands
        await searchBandsOnly();
      } else {
        // Search users with specific userType
        await searchUsersOnly();
      }
      
      setHasSearched(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to search all collections (users and bands)
  const searchAllCollections = async () => {
    // Search users
    let userResults = await searchCollection('users_index');
    
    // Search bands
    let bandResults = await searchCollection('bands_index');
    
    // Combine and sort by relevance (we'll use Algolia's default sorting)
    setSearchResults([...userResults, ...bandResults]);
  };
  
  // Function to search only bands
  const searchBandsOnly = async () => {
    const bandResults = await searchCollection('bands_index');
    setSearchResults(bandResults);
  };
  
  // Function to search only users
  const searchUsersOnly = async () => {
    const userResults = await searchCollection('users_index');
    setSearchResults(userResults);
  };
  
  // Generic function to search a specific collection
  const searchCollection = async (indexName) => {
    let algoliaFilters = [];
    
    // For users_index, add user type filter
    if (indexName === 'users_index' && activeSearchTab !== 'All') {
      // If user is Staff, always search for Organizations only
      if (currentUser?.userType === 'Staff') {
        algoliaFilters.push(`userType:"Organization"`);
      } else if (activeSearchTab !== 'Bands') {
        // For standard user types
        algoliaFilters.push(`userType:"${activeSearchTab}"`);
      }
    }
    
    // Add filters for subtypes (if any)
    if (searchFilters.subTypes && searchFilters.subTypes.length > 0) {
      const subTypeFilters = searchFilters.subTypes.map(subType => 
        `${indexName === 'bands_index' ? 'bandGenres' : 'userSubType'}:"${subType}"`
      ).join(' OR ');
      algoliaFilters.push(`(${subTypeFilters})`);
    }
    
    // Add location filters
    if (searchFilters.country) {
      algoliaFilters.push(`${indexName === 'bands_index' ? 'bandCountry' : 'country'}:"${searchFilters.country}"`);
    }
    if (searchFilters.state) {
      algoliaFilters.push(`${indexName === 'bands_index' ? 'bandState' : 'state'}:"${searchFilters.state}"`);
    }
    if (searchFilters.city) {
      algoliaFilters.push(`${indexName === 'bands_index' ? 'bandCity' : 'city'}:"${searchFilters.city}"`);
    }
    
    const filterString = algoliaFilters.length > 0 ? algoliaFilters.join(' AND ') : '';
    
    const { results } = await client.search([{
      indexName,
      query: searchTerm,
      filters: filterString,
      hitsPerPage: 10
    }]);
    
    // Format results based on the index
    return results[0].hits.map(hit => {
      if (indexName === 'bands_index') {
        return {
          userId: hit.objectID, // Using userId for consistent keys
          id: hit.objectID,
          fullName: hit.bandFullName,
          userName: hit.bandUserName,
          userSubType: hit.bandGenres?.join(', ') || '',
          profilePictureUrl: hit.bandProfilePictureUrl,
          userType: 'Band', // Mark as band for UI
          idVerificationStatus: hit.verified ? 'Verified' : '',
          city: hit.bandCity,
          state: hit.bandState,
          country: hit.bandCountry,
          isBand: true // Flag to differentiate bands
        };
      } else {
        return {
          userId: hit.objectID,
          fullName: hit.fullName,
          userName: hit.userName,
          userSubType: hit.userSubType,
          profilePictureUrl: hit.profilePictureUrl,
          userType: hit.userType,
          idVerificationStatus: hit.idVerificationStatus,
          city: hit.city,
          state: hit.state,
          country: hit.country,
          isBand: false
        };
      }
    });
  };
  
  // Check if all required discovery filters are set
  const validateDiscoveryFilters = () => {
    if (!discoveryDate) {
      setValidationError("Please select a date");
      return false;
    }
    if (!selectedUserType) {
      setValidationError("Please select a user type");
      return false;
    }
    if (selectedSubTypes.length === 0) {
      setValidationError("Please select at least one specialty");
      return false;
    }
    if (!selectedCountry) {
      setValidationError("Please select a country");
      return false;
    }
    if (!selectedState) {
      setValidationError("Please select a state/province");
      return false;
    }
    if (!selectedCity) {
      setValidationError("Please select a city");
      return false;
    }
    
    setValidationError(null);
    return true;
  };
  
  // Try to get cached available users
  const getCachedAvailableUsers = async (filters, date) => {
    try {
      // Generate document ID for the cache
      const docId = generateCacheDocId(filters, date);
      
      // Check if there's a cached document
      const db = getFirestoreInstance();
      const cacheRef = doc(db, 'availableUsersByDate', docId);
      const cacheDoc = await getDoc(cacheRef);
      
      if (cacheDoc.exists()) {
        const cacheData = cacheDoc.data();
        
        // Check if cache is still valid (less than 48 hours old)
        const lastCheckedAt = cacheData.lastCheckedAt?.toDate();
        const now = new Date();
        
        if (lastCheckedAt && differenceInHours(now, lastCheckedAt) < 48) {
          console.log('Using cached available users data');
          return {
            users: cacheData.users || [],
            allUsersQueried: cacheData.allUsersQueried || false,
            lastScannedUserId: cacheData.lastScannedUserId || null
          };
        } else {
          console.log('Cache is more than 48 hours old, refreshing');
          return null;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error getting cached available users:', error);
      return null;
    }
  };
  
  // Save available users to cache
  const cacheAvailableUsers = async (filters, date, users, allUsersQueried, lastScannedUserId) => {
    try {
      // Generate document ID for the cache
      const docId = generateCacheDocId(filters, date);
      
      // Create cache document
      const db = getFirestoreInstance();
      const cacheRef = doc(db, 'availableUsersByDate', docId);
      
      // Save data to cache
      await setDoc(cacheRef, {
        users: users.slice(0, 50), // Store up to 50 users
        allUsersQueried,
        lastCheckedAt: serverTimestamp(),
        lastScannedUserId,
        filters: {
          city: filters.city || '',
          state: filters.state || '',
          country: filters.country || '',
          userType: filters.userType || '',
          userSubType: Array.isArray(filters.userSubType) ? filters.userSubType : [filters.userSubType],
          date
        }
      });
      
      console.log('Cached available users data');
    } catch (error) {
      console.error('Error caching available users:', error);
    }
  };
  
  // Check if a user is available on a specific date
  const checkUserAvailability = async (userId, date) => {
    try {
      // Query user_event_dates collection to check if user is busy on this date
      const db = getFirestoreInstance();
      const q = query(
        collection(db, 'user_event_dates'),
        where('userId', '==', userId),
        where('date', '==', date)
      );
      
      const snapshot = await getDocs(q);
      
      // If no documents found, user is available
      return snapshot.empty;
    } catch (error) {
      console.error(`Error checking availability for user ${userId}:`, error);
      return false; // Assume user is not available in case of error
    }
  };

  // Check if a band is available on a specific date
  const checkBandAvailability = async (bandId, date) => {
    try {
      // Query band_event_dates collection to check if band is busy on this date
      const db = getFirestoreInstance();
      const q = query(
        collection(db, 'band_event_dates'),
        where('bandId', '==', bandId),
        where('date', '==', date)
      );
      
      const snapshot = await getDocs(q);
      
      // If no documents found, band is available
      return snapshot.empty;
    } catch (error) {
      console.error(`Error checking availability for band ${bandId}:`, error);
      return false; // Assume band is not available in case of error
    }
  };
  
  // Get users by filters with pagination
  const getUsersByFilters = async (filters, startAfterId = null, pageSize = 10) => {
    try {
      const db = getFirestoreInstance();
      
      // Start building the query
      let userQuery;
      
      // Base query with required filters
      if (startAfterId) {
        userQuery = query(
          collection(db, 'users'),
          where('userType', '==', filters.userType),
          where('userSubType', '==', filters.userSubType[0]), // For now, use only the first subtype
          where('country', '==', filters.country),
          where('state', '==', filters.state),
          where('city', '==', filters.city),
          orderBy('userId'), // Needed for startAfter to work
          startAfter(startAfterId),
          limit(pageSize)
        );
      } else {
        userQuery = query(
          collection(db, 'users'),
          where('userType', '==', filters.userType),
          where('userSubType', '==', filters.userSubType[0]), // For now, use only the first subtype
          where('country', '==', filters.country),
          where('state', '==', filters.state),
          where('city', '==', filters.city),
          orderBy('userId'),
          limit(pageSize)
        );
      }
      
      // Execute query
      const snapshot = await getDocs(userQuery);
      
      // Return users and lastScannedUserId
      if (snapshot.empty) {
        return {
          users: [],
          lastScannedUserId: null,
          allUsersQueried: true // No more users matching the filters
        };
      }
      
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      const lastScannedId = lastDoc.id;
      
      const users = snapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data()
      }));
      
      return {
        users,
        lastScannedUserId: lastScannedId,
        allUsersQueried: users.length < pageSize // If we got fewer users than requested, all users have been queried
      };
    } catch (error) {
      console.error('Error getting users by filters:', error);
      return { users: [], lastScannedUserId: null, allUsersQueried: false };
    }
  };

  // Get bands by filters with pagination
  const getBandsByFilters = async (filters, startAfterId = null, pageSize = 10) => {
    try {
      const db = getFirestoreInstance();
      
      // Start building the query for bands
      let bandQuery;
      
      // Base query with required filters
      if (startAfterId) {
        bandQuery = query(
          collection(db, 'bands'),
          // Use bandGenres for subType matching
          where('bandGenres', 'array-contains', filters.userSubType[0]), 
          where('bandCountry', '==', filters.country),
          where('bandState', '==', filters.state),
          where('bandCity', '==', filters.city),
          orderBy('bandId'), // Needed for startAfter to work
          startAfter(startAfterId),
          limit(pageSize)
        );
      } else {
        bandQuery = query(
          collection(db, 'bands'),
          where('bandGenres', 'array-contains', filters.userSubType[0]),
          where('bandCountry', '==', filters.country),
          where('bandState', '==', filters.state),
          where('bandCity', '==', filters.city),
          orderBy('bandId'),
          limit(pageSize)
        );
      }
      
      // Execute query
      const snapshot = await getDocs(bandQuery);
      
      // Return bands and lastScannedId
      if (snapshot.empty) {
        return {
          bands: [],
          lastScannedBandId: null,
          allBandsQueried: true
        };
      }
      
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      const lastScannedId = lastDoc.id;
      
      const bands = snapshot.docs.map(doc => ({
        userId: doc.id, // Use userId for consistency with user objects
        id: doc.id,
        fullName: doc.data().bandFullName,
        userName: doc.data().bandUserName,
        userSubType: doc.data().bandGenres?.join(', ') || '',
        profilePictureUrl: doc.data().bandProfilePictureUrl,
        userType: 'Band',
        city: doc.data().bandCity,
        state: doc.data().bandState,
        country: doc.data().bandCountry,
        isBand: true
      }));
      
      return {
        bands,
        lastScannedBandId: lastScannedId,
        allBandsQueried: bands.length < pageSize
      };
    } catch (error) {
      console.error('Error getting bands by filters:', error);
      return { bands: [], lastScannedBandId: null, allBandsQueried: false };
    }
  };
  
  // Handle discovery with smart search count increment
  const handleDiscovery = async (loadMore = false) => {
    // Check if user can use discovery (only Basic+ tiers)
    if (currentUser?.membershipTier === 'Free') return;
    
    // Check if user has exceeded discovery limit
    if (discoveryCount >= discoveryLimit) {
      setLimitModalType('discovery');
      setIsLimitModalOpen(true);
      return;
    }
    
    // Validate all required filters are set
    if (!validateDiscoveryFilters()) {
      return;
    }
    
    // Handle load more case differently - always increment for load more
    if (loadMore && discoveryPage > 1) {
      const canProceed = await incrementSearchCount('discovery');
      if (!canProceed) return;
      
      setIsLoading(true);
      try {
        // Create filters object
        const discoveryFilters = {
          userType: selectedUserType,
          userSubType: selectedSubTypes,
          country: selectedCountry?.name,
          state: selectedState?.name,
          city: selectedCity?.name
        };
        
        // If all users have been queried, just return
        if (allUsersQueried) {
          setIsLoading(false);
          return;
        }
        
        // If selected type is Band, use band discovery logic
        if (selectedUserType === 'Band') {
          await handleBandDiscovery(discoveryFilters, true);
        } else {
          // For other user types, use standard discovery logic
          await handleUserDiscovery(discoveryFilters, true);
        }
        
        setHasDiscovered(true);
      } catch (error) {
        console.error('Discovery error on load more:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // For new discovery searches, check if params have changed
    const currentDiscoveryParams = {
      userType: selectedUserType,
      subTypes: [...selectedSubTypes], // Make a copy to ensure proper comparison
      country: selectedCountry?.name || '',
      state: selectedState?.name || '',
      city: selectedCity?.name || '',
      date: discoveryDate
    };
    
    // Check if this is the same discovery as last time
    const isSameDiscovery = 
      currentDiscoveryParams.userType === lastDiscoveryParams.userType &&
      JSON.stringify(currentDiscoveryParams.subTypes.sort()) === JSON.stringify(lastDiscoveryParams.subTypes.sort()) &&
      currentDiscoveryParams.country === lastDiscoveryParams.country &&
      currentDiscoveryParams.state === lastDiscoveryParams.state &&
      currentDiscoveryParams.city === lastDiscoveryParams.city &&
      currentDiscoveryParams.date === lastDiscoveryParams.date;
    
    // Only increment count if this is a new discovery search
    let canProceed = true;
    if (!isSameDiscovery) {
      canProceed = await incrementSearchCount('discovery');
      if (!canProceed) return;
      
      // Update last discovery params after successful increment
      setLastDiscoveryParams(currentDiscoveryParams);
    }
    
    setIsLoading(true);
    try {
      // Create filters object
      const discoveryFilters = {
        userType: selectedUserType,
        userSubType: selectedSubTypes,
        country: selectedCountry?.name,
        state: selectedState?.name,
        city: selectedCity?.name
      };
      
      // If selected type is Band, use band discovery logic
      if (selectedUserType === 'Band') {
        await handleBandDiscovery(discoveryFilters, false); // false = not load more
      } else {
        // For other user types, use standard discovery logic
        await handleUserDiscovery(discoveryFilters, false); // false = not load more
      }
      
      setHasDiscovered(true);
    } catch (error) {
      console.error('Discovery error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle band-specific discovery
  const handleBandDiscovery = async (filters, loadMore) => {
    // If loading more, use the current state
    if (loadMore && discoveryPage > 1) {
      // If all bands have been queried, just return
      if (allUsersQueried) {
        return;
      }
      
      // Get next batch of bands
      const { bands, lastScannedBandId, allBandsQueried } = 
        await getBandsByFilters(filters, lastScannedUserId);
      
      // Check availability for each band
      const availableBands = [];
      for (const band of bands) {
        const isAvailable = await checkBandAvailability(band.id, discoveryDate);
        if (isAvailable) {
          availableBands.push(band);
        }
      }
      
      // Update state
      setDiscoveryResults(prev => [...prev, ...availableBands]);
      setLastScannedUserId(lastScannedBandId);
      setAllUsersQueried(allBandsQueried);
      setDiscoveryPage(prev => prev + 1);
      setHasMoreDiscoveryResults(!allBandsQueried);
      
      // Cache results if needed
      if (discoveryResults.length + availableBands.length <= 50) {
        await cacheAvailableUsers(
          filters, 
          discoveryDate, 
          [...discoveryResults, ...availableBands], 
          allBandsQueried,
          lastScannedBandId
        );
      }
    } else {
      // First try to get cached results
      const cachedData = await getCachedAvailableUsers(filters, discoveryDate);
      
      if (cachedData && cachedData.users.length > 0) {
        // Use cached data
        setDiscoveryResults(cachedData.users.slice(0, 10)); // Show first 10 bands
        setLastScannedUserId(cachedData.lastScannedUserId);
        setAllUsersQueried(cachedData.allUsersQueried);
        setDiscoveryPage(2); // Start from page 2 next time
        setHasMoreDiscoveryResults(cachedData.users.length > 10 || !cachedData.allUsersQueried);
      } else {
        // No cache or cache is empty - query bands directly
        const { bands, lastScannedBandId, allBandsQueried } = 
          await getBandsByFilters(filters);
        
        // Check availability for each band
        const availableBands = [];
        for (const band of bands) {
          const isAvailable = await checkBandAvailability(band.id, discoveryDate);
          if (isAvailable) {
            availableBands.push(band);
          }
        }
        
        // Update state
        setDiscoveryResults(availableBands);
        setLastScannedUserId(lastScannedBandId);
        setAllUsersQueried(allBandsQueried);
        setDiscoveryPage(2); // Start from page 2 next time
        setHasMoreDiscoveryResults(!allBandsQueried);
        
        // Cache results
        await cacheAvailableUsers(
          filters, 
          discoveryDate, 
          availableBands, 
          allBandsQueried,
          lastScannedBandId
        );
      }
    }
  };

  // Handle standard user discovery
  const handleUserDiscovery = async (filters, loadMore) => {
    // If loading more, use the current page and last scanned user ID
    if (loadMore && discoveryPage > 1 && lastScannedUserId) {
      // If all users have been queried and we have cached results, just return
      if (allUsersQueried) {
        return;
      }
      
      // Get next batch of users
      const { users, lastScannedUserId: newLastScannedId, allUsersQueried: newAllUsersQueried } = 
        await getUsersByFilters(filters, lastScannedUserId);
      
      // Check availability for each user
      const availableUsers = [];
      for (const user of users) {
        const isAvailable = await checkUserAvailability(user.userId, discoveryDate);
        if (isAvailable) {
          availableUsers.push({
            userId: user.userId,
            fullName: user.fullName,
            userName: user.userName,
            userSubType: user.userSubType,
            profilePictureUrl: user.profilePictureUrl,
            userType: user.userType,
            idVerificationStatus: user.idVerificationStatus,
            city: user.city,
            state: user.state,
            country: user.country,
          });
        }
      }
      
      // Update state
      setDiscoveryResults(prev => [...prev, ...availableUsers]);
      setLastScannedUserId(newLastScannedId);
      setAllUsersQueried(newAllUsersQueried);
      setDiscoveryPage(prev => prev + 1);
      setHasMoreDiscoveryResults(!newAllUsersQueried);
      
      // Cache results if needed
      if (discoveryResults.length + availableUsers.length <= 50) {
        await cacheAvailableUsers(
          filters, 
          discoveryDate, 
          [...discoveryResults, ...availableUsers], 
          newAllUsersQueried,
          newLastScannedId
        );
      }
    } else {
      // First try to get cached results
      const cachedData = await getCachedAvailableUsers(filters, discoveryDate);
      
      if (cachedData && cachedData.users.length > 0) {
        // Use cached data
        setDiscoveryResults(cachedData.users.slice(0, 10)); // Show first 10 users
        setLastScannedUserId(cachedData.lastScannedUserId);
        setAllUsersQueried(cachedData.allUsersQueried);
        setDiscoveryPage(2); // Start from page 2 next time
        setHasMoreDiscoveryResults(cachedData.users.length > 10 || !cachedData.allUsersQueried);
      } else {
        // No cache or cache is empty - query users directly
        const { users, lastScannedUserId: newLastScannedId, allUsersQueried: newAllUsersQueried } = 
          await getUsersByFilters(filters);
        
        // Check availability for each user
        const availableUsers = [];
        for (const user of users) {
          const isAvailable = await checkUserAvailability(user.userId, discoveryDate);
          if (isAvailable) {
            availableUsers.push({
              userId: user.userId,
              fullName: user.fullName,
              userName: user.userName,
              userSubType: user.userSubType,
              profilePictureUrl: user.profilePictureUrl,
              userType: user.userType,
              idVerificationStatus: user.idVerificationStatus,
              city: user.city,
              state: user.state,
              country: user.country,
            });
          }
        }
        
        // Update state
        setDiscoveryResults(availableUsers);
        setLastScannedUserId(newLastScannedId);
        setAllUsersQueried(newAllUsersQueried);
        setDiscoveryPage(2); // Start from page 2 next time
        setHasMoreDiscoveryResults(!newAllUsersQueried);
        
        // Cache results
        await cacheAvailableUsers(
          filters, 
          discoveryDate, 
          availableUsers, 
          newAllUsersQueried,
          newLastScannedId
        );
      }
    }
  };

  const handleTabChange = async (tab) => {
    setActiveSearchTab(tab);
    
    // Re-run search if we've already searched
    if (activeMainTab === 'search' && hasSearched) {
      await handleSearch();
    }
  };
  
  const handleMainTabChange = (tab) => {
    setActiveMainTab(tab);
    // Reset states when switching tabs
    if (tab === 'search') {
      setHasDiscovered(false);
      setDiscoveryResults([]);
      setValidationError(null);
      
      // Don't reset search filters
    } else {
      setHasSearched(false);
      setSearchResults([]);
      
      // Don't reset discovery filters
    }
  };

  const handleUserSelect = async (user) => {
    setIsLoading(true);
    
    try {
      // Pre-fetch the user profile data
      if (user.userId !== currentUser?.userId) {
        try {
          if (user.isBand) {
            // Navigate to band page
            navigate(`/bands/${user.userName}`);
          } else {
            // Pre-fetch user data and navigate to user profile
            await getProfileData(user.userId);
            console.log("User profile data pre-fetched successfully");
            
            // Add to recently viewed users
            const updatedRecentUsers = [user, ...recentlyViewedUsers.filter(u => u.userId !== user.userId)].slice(0, 10);
            setRecentlyViewedUsers(updatedRecentUsers);
            localStorage.setItem(RECENT_USERS_CACHE_KEY, JSON.stringify(updatedRecentUsers));
            
            // Navigate to user profile
            navigate(`/${user.userName}`);
          }
        } catch (error) {
          console.error("Failed to pre-fetch profile data:", error);
        }
      } else {
        // Navigate to own profile
        navigate(`/${user.userName}`);
      }
    } catch (error) {
      console.error('Error navigating to profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/membership');
  };

  // Handle date selection in discovery tab
  const handleDateChange = (e) => {
    const dateValue = e.target.value;
    setDiscoveryDate(dateValue);
    
    // Reset discovery results when date changes
    setDiscoveryResults([]);
    setLastScannedUserId(null);
    setAllUsersQueried(false);
    setHasDiscovered(false);
    setDiscoveryPage(1);
  };

  // Handle user type selection in discovery tab
  const handleUserTypeChange = (value) => {
    setSelectedUserType(value);
    setSelectedSubTypes([]);
    
    // Reset discovery results when user type changes
    setDiscoveryResults([]);
    setLastScannedUserId(null);
    setAllUsersQueried(false);
    setHasDiscovered(false);
    setDiscoveryPage(1);
  };

  // Handle country selection
  const handleCountryChange = (countryCode) => {
    const countryObj = Country.getCountryByCode(countryCode);
    setSelectedCountry(countryObj);
    setSelectedState(null);
    setSelectedCity(null);
    setAvailableCities([]);
    
    if (countryObj) {
      setTempFilters(prev => ({ ...prev, country: countryObj.name }));
      const states = State.getStatesOfCountry(countryCode);
      setAvailableStates(states);
    } else {
      setTempFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.country;
        delete newFilters.state;
        delete newFilters.city;
        return newFilters;
      });
      setAvailableStates([]);
    }
    
    // Reset discovery results when country changes
    if (activeMainTab === 'discover') {
      setDiscoveryResults([]);
      setLastScannedUserId(null);
      setAllUsersQueried(false);
      setHasDiscovered(false);
      setDiscoveryPage(1);
    }
  };
  
  // Handle state selection
  const handleStateChange = (stateCode) => {
    if (!selectedCountry) return;
    
    const stateObj = State.getStateByCodeAndCountry(stateCode, selectedCountry.isoCode);
    setSelectedState(stateObj);
    setSelectedCity(null);
    
    if (stateObj) {
      setTempFilters(prev => ({ ...prev, state: stateObj.name }));
      const cities = City.getCitiesOfState(selectedCountry.isoCode, stateCode);
      setAvailableCities(cities);
    } else {
      setTempFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.state;
        delete newFilters.city;
        return newFilters;
      });
      setAvailableCities([]);
    }
    
    // Reset discovery results when state changes
    if (activeMainTab === 'discover') {
      setDiscoveryResults([]);
      setLastScannedUserId(null);
      setAllUsersQueried(false);
      setHasDiscovered(false);
      setDiscoveryPage(1);
    }
  };
  
  // Handle city selection
  const handleCityChange = (cityName) => {
    if (!selectedState || !selectedCountry) return;
    
    const cityObj = availableCities.find(city => city.name === cityName);
    setSelectedCity(cityObj);
    
    if (cityObj) {
      setTempFilters(prev => ({ ...prev, city: cityObj.name }));
    } else {
      setTempFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.city;
        return newFilters;
      });
    }
    
    // Reset discovery results when city changes
    if (activeMainTab === 'discover') {
      setDiscoveryResults([]);
      setLastScannedUserId(null);
      setAllUsersQueried(false);
      setHasDiscovered(false);
      setDiscoveryPage(1);
    }
  };
  
  // Handle SubType selection
  const handleSubTypeSelection = (subType) => {
    if (!selectedSubTypes.includes(subType)) {
      setSelectedSubTypes(prev => [...prev, subType]);
    }
    setSubTypeSearchTerm('');
    
    // Reset discovery results when subtype changes
    if (activeMainTab === 'discover') {
      setDiscoveryResults([]);
      setLastScannedUserId(null);
      setAllUsersQueried(false);
      setHasDiscovered(false);
      setDiscoveryPage(1);
    }
  };
  
  // Remove selected SubType
  const removeSelectedSubType = (subType) => {
    setSelectedSubTypes(prev => prev.filter(type => type !== subType));
    
    // Reset discovery results when subtype changes
    if (activeMainTab === 'discover') {
      setDiscoveryResults([]);
      setLastScannedUserId(null);
      setAllUsersQueried(false);
      setHasDiscovered(false);
      setDiscoveryPage(1);
    }
  };
  
  // Apply filters
  const applyFilters = () => {
    if (activeMainTab === 'search') {
      const newFilters = { ...searchTempFilters };
      
      if (selectedSubTypes.length > 0) {
        newFilters.subTypes = selectedSubTypes;
      } else {
        delete newFilters.subTypes;
      }
      
      setSearchFilters(newFilters);
      setFilters(newFilters); // Keep for backward compatibility
    } else {
      // For discover tab
      const newFilters = {
        userType: selectedUserType,
        userSubType: selectedSubTypes,
        country: selectedCountry?.name,
        state: selectedState?.name,
        city: selectedCity?.name,
        date: discoveryDate
      };
      
      setDiscoverFilters(newFilters);
    }
    
    setIsModalOpen(false);
    
    // Run search only if in search tab
    if (activeMainTab === 'search') {
      handleSearch();
    }
  };
  
  // Remove filter
  const removeFilter = (key, value) => {
    const newFilters = { ...searchFilters };
    
    if (key === 'subTypes') {
      const subTypes = newFilters[key].filter(type => type !== value);
      if (subTypes.length === 0) {
        delete newFilters[key];
        setSelectedSubTypes([]);
      } else {
        newFilters[key] = subTypes;
        setSelectedSubTypes(subTypes);
      }
    } else {
      delete newFilters[key];
      
      // Also update the corresponding state variables
      if (key === 'country') {
        setSelectedCountry(null);
        setSelectedState(null);
        setSelectedCity(null);
      } else if (key === 'state') {
        setSelectedState(null);
        setSelectedCity(null);
      } else if (key === 'city') {
        setSelectedCity(null);
      }
    }
    
    setSearchFilters(newFilters);
    setFilters(newFilters); // Keep this for backward compatibility
    
    // Run search
    handleSearch();
  };
  
  // Remove from recently viewed
  const removeFromRecentlyViewed = (userId) => {
    const updatedRecentUsers = recentlyViewedUsers.filter(user => user.userId !== userId);
    setRecentlyViewedUsers(updatedRecentUsers);
    localStorage.setItem(RECENT_USERS_CACHE_KEY, JSON.stringify(updatedRecentUsers));
  };
  
  // Clear all recently viewed users
  const clearRecentlyViewedUsers = () => {
    setRecentlyViewedUsers([]);
    localStorage.removeItem(RECENT_USERS_CACHE_KEY);
  };

  // Helper to get progress percentage for search/discovery limits
  const getLimitProgress = (count, limit) => {
    return Math.min(Math.round((count / limit) * 100), 100);
  };

  // Get the tabs that should be displayed based on user type
  const availableTabs = getAvailableTabs();

  // Check if search limit is reached
  const isSearchLimitReached = () => {
    return searchCount >= searchLimit;
  };

  // Check if discovery limit is reached
  const isDiscoveryLimitReached = () => {
    return discoveryCount >= discoveryLimit;
  };

  // Get remaining searches
  const getRemainingSearches = () => {
    return Math.max(0, searchLimit - searchCount);
  };

  // Get remaining discoveries
  const getRemainingDiscoveries = () => {
    return Math.max(0, discoveryLimit - discoveryCount);
  };

  return (
    <div className={styles.mainLayout}>
  
      <LeftMenu />
      <div className={styles.searchContainer}>
        <AuthCheckWrapper>
          <div className={styles.mainTabs}>
            <button
              className={`${styles.mainTab} ${activeMainTab === 'search' ? styles.active : ''}`}
              onClick={() => handleMainTabChange('search')}
            >
              <FontAwesomeIcon icon={faSearch} className={styles.tabIcon} />
              Search
            </button>
            <button
              className={`${styles.mainTab} ${activeMainTab === 'discover' ? styles.active : ''}`}
              onClick={() => handleMainTabChange('discover')}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.tabIcon} />
              Discover
            </button>
          </div>
          
          {/* Search limit indicator */}
          {currentUser?.membershipTier !== 'Free' && (
            <div className={styles.limitIndicator}>
              <div className={styles.limitLabel}>
                {activeMainTab === 'search' ? (
                  <>
                    <span>Search Limit:</span> 
                    <strong>{getRemainingSearches()} of {searchLimit} remaining</strong>
                  </>
                ) : (
                  <>
                    <span>Discovery Limit:</span>
                    <strong>{getRemainingDiscoveries()} of {discoveryLimit} remaining</strong>
                  </>
                )}
              </div>
              <div className={styles.limitProgressBar}>
                <div 
                  className={styles.limitProgress}
                  style={{ 
                    width: `${activeMainTab === 'search' 
                      ? getLimitProgress(searchCount, searchLimit) 
                      : getLimitProgress(discoveryCount, discoveryLimit)}%` 
                  }}
                ></div>
              </div>
            </div>
          )}
          
          {activeMainTab === 'search' ? (
            // Search Tab Header
            <div className={styles.searchHeader}>
              <div className={styles.searchBar}>
                <div 
                  className={`${styles.filterIconContainer} ${isSearchLimitReached() ? styles.disabled : ''}`}
                  onClick={() => !isSearchLimitReached() && setIsModalOpen(true)}
                >
                  <FontAwesomeIcon 
                    icon={faFilter} 
                    className={styles.filterIcon}
                  />
                  <span className={styles.filterText}>Filter</span>
                </div>
                
                <input
                  type="text"
                  placeholder="Search users and bands..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  disabled={isSearchLimitReached()}
                />
                
                {searchTerm && (
                  <FontAwesomeIcon 
                    icon={faTimes} 
                    className={styles.clearIcon}
                    onClick={() => setSearchTerm('')}
                  />
                )}
                
                <button 
                  className={`${styles.searchButton} ${((!searchTerm.trim() && Object.keys(filters).length === 0) || isSearchLimitReached()) ? styles.disabled : ''}`}
                  onClick={handleSearch}
                  disabled={(!searchTerm.trim() && Object.keys(filters).length === 0) || isSearchLimitReached()}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  <span>Search</span>
                </button>
              </div>

              <div className={styles.filterTags}>
                {Object.entries(filters).map(([key, value]) => {
                  if (key === 'subTypes') {
                    return value.map((subType, index) => (
                      <div key={`${key}-${index}`} className={styles.filterTag}>
                        <span>{subType}</span>
                        <button onClick={() => removeFilter(key, subType)}>×</button>
                      </div>
                    ));
                  } else {
                    return (
                      <div key={key} className={styles.filterTag}>
                        <span>{`${key}: ${value}`}</span>
                        <button onClick={() => removeFilter(key, value)}>×</button>
                      </div>
                    );
                  }
                })}
              </div>

              <div className={styles.tabs}>
                {availableTabs.map((tab) => (
                  <button
                    key={tab}
                    className={`${styles.tab} ${activeSearchTab === tab ? styles.active : ''}`}
                    onClick={() => handleTabChange(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // Discover Tab Header
            <div className={styles.discoverHeader}>
              <div className={styles.discoverFilters}>
                <div className={styles.discoverFilterGroup}>
                  <label>Professional Type</label>
                  <select
                    value={selectedUserType}
                    onChange={(e) => handleUserTypeChange(e.target.value)}
                    className={styles.discoverSelect}
                    disabled={currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()}
                  >
                    {[
                      ...userTypeOptions.filter(option => option.value !== 'Organization'),
                      { label: "Staff", value: "Staff" } // Add Staff option for discovery
                    ].map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                   
                <div className={styles.discoverFilterGroup}>
                  <label>Specialization</label>
                  <div className={styles.discoverSubTypeContainer} ref={discoverSubTypeDropdownRef}>
                    <div 
                      className={styles.discoverSubTypeHeader} 
                      onClick={() => !(currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()) && setIsDiscoverSubTypeDropdownOpen(!isDiscoverSubTypeDropdownOpen)}
                    >
                      <span>{selectedSubTypes.length > 0 ? `${selectedSubTypes.length} selected` : 'Select specialty'}</span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`${styles.dropdownIcon} ${isDiscoverSubTypeDropdownOpen ? styles.open : ''}`} 
                      />
                    </div>
                    
                    {isDiscoverSubTypeDropdownOpen && (
                      <div className={styles.discoverSubTypeDropdown}>
                        <div className={styles.subTypeSearchContainer}>
                          <input
                            type="text"
                            placeholder="Search specialties..."
                            value={subTypeSearchTerm}
                            onChange={(e) => setSubTypeSearchTerm(e.target.value)}
                            className={styles.subTypeSearchInput}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        
                        <div className={styles.subTypeOptionsList}>
                          {filteredSubTypes.map(subType => (
                            <div 
                              key={subType.value} 
                              className={`${styles.subTypeOption} ${selectedSubTypes.includes(subType.label) ? styles.selected : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubTypeSelection(subType.label);
                              }}
                            >
                              <span>{subType.label}</span>
                              {selectedSubTypes.includes(subType.label) && (
                                <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                              )}
                            </div>
                          ))}
                          
                          {filteredSubTypes.length === 0 && (
                            <div className={styles.noSubTypeResults}>
                              No matches found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={styles.discoverFilterGroup}>
                  <label>Country</label>
                  <select
                    value={selectedCountry?.isoCode || ''}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className={styles.discoverSelect}
                    disabled={currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()}
                  >
                    <option value="">Select Country</option>
                    {Country.getAllCountries().map((country) => (
                      <option key={country.isoCode} value={country.isoCode}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.discoverFilterGroup}>
                  <label>State/Province</label>
                  <select
                    value={selectedState?.isoCode || ''}
                    onChange={(e) => handleStateChange(e.target.value)}
                    className={styles.discoverSelect}
                    disabled={!selectedCountry || currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()}
                  >
                    <option value="">Select State</option>
                    {availableStates.map((state) => (
                      <option key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.discoverFilterGroup}>
                  <label>City</label>
                  <select
                    value={selectedCity?.name || ''}
                    onChange={(e) => handleCityChange(e.target.value)}
                    className={styles.discoverSelect}
                    disabled={!selectedState || currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()}
                  >
                    <option value="">Select City</option>
                    {availableCities.map((city) => (
                      <option key={`${city.name}-${city.stateCode}`} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.discoverFilterGroup}>
                  <label>Available Date</label>
                  <div className={styles.datePickerWrapper}>
                    <input
                      type="date"
                      value={discoveryDate}
                      onChange={handleDateChange}
                      className={styles.discoverDatePicker}
                      disabled={currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()}
                      onClick={(e) => {
                        // Focus and open the calendar when the field is clicked anywhere
                        e.target.showPicker && e.target.showPicker();
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className={styles.selectedSubtypes}>
                {selectedSubTypes.map((subType) => (
                  <div key={subType} className={styles.subtypeTag}>
                    <span>{subType}</span>
                    <button 
                      onClick={() => removeSelectedSubType(subType)}
                      disabled={currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()}
                    >×</button>
                  </div>
                ))}
              </div>
              
              {validationError && (
                <div className={styles.validationError}>
                  <FontAwesomeIcon icon={faExclamationTriangle} className={styles.validationIcon} />
                  <span>{validationError}</span>
                </div>
              )}
              
              <div className={styles.discoverActions}>
                <button 
                  className={styles.discoverButton}
                  onClick={() => handleDiscovery()}
                  disabled={
                    currentUser?.membershipTier === 'Free' || 
                    isDiscoveryLimitReached() ||
                    isLoading || 
                    !discoveryDate || 
                    !selectedUserType || 
                    selectedSubTypes.length === 0 || 
                    !selectedCountry || 
                    !selectedState || 
                    !selectedCity
                  }
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Find Available {selectedUserType === 'Band' ? 'Bands' : `${selectedUserType}s`}
                </button>
              </div>
            </div>
          )}

          <div className={styles.searchResultsUpdated}>
            {activeMainTab === 'search' && isSearchLimitReached() ? (
              <div className={styles.upgradePrompt}>
                <FontAwesomeIcon icon={faLock} className={styles.upgradeIcon} />
                <h3>Search Limit Reached</h3>
                <p>You've used all your {searchLimit} searches for this month.</p>
                <button className={styles.upgradeButton} onClick={handleUpgrade}>
                  <FontAwesomeIcon icon={faRocket} />
                  Upgrade Membership
                </button>
              </div>
            ) : activeMainTab === 'discover' && (currentUser?.membershipTier === 'Free' || isDiscoveryLimitReached()) ? (
              <div className={styles.upgradePrompt}>
                <FontAwesomeIcon icon={faLock} className={styles.upgradeIcon} />
                <h3>
                  {currentUser?.membershipTier === 'Free' 
                    ? 'Discover Feature Unavailable' 
                    : 'Discovery Limit Reached'}
                </h3>
                <p>
                  {currentUser?.membershipTier === 'Free'
                    ? 'The Discover feature is only available for Basic plans and above.' 
                    : `You've used all your ${discoveryLimit} discoveries for this month.`}
                </p>
                <button className={styles.upgradeButton} onClick={handleUpgrade}>
                  <FontAwesomeIcon icon={faRocket} />
                  Upgrade Membership
                </button>
              </div>
            ) : isLoading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Loading...</p>
              </div>
            ) : activeMainTab === 'search' ? (
              // Search results section
              hasSearched ? (
                searchResults.length > 0 ? (
                  <div className={styles.resultsGrid}>
                    {searchResults.map(user => (
                      <div 
                        key={user.userId} 
                        className={styles.userCard}
                        onClick={() => handleUserSelect(user)}
                      >
                        <div className={styles.userCardContent}>
                          <img 
                            src={user.profilePictureUrl || defaultProfilePhoto}
                            alt={user.fullName}
                            className={styles.profilePhoto}
                          />
                          <div className={styles.userInfo}>
                            <div className={styles.nameContainer}>
                              <h3>{user.fullName}</h3>
                              {(user.idVerificationStatus === 'Verified' || (user.isBand && user.verified)) && (
                                <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                              )}
                            </div>
                            <p className={styles.username}>@{user.userName}</p>
                            <p className={styles.subtype}>
                              {user.isBand ? (
                                <FontAwesomeIcon icon={faMusic} className={styles.bandIcon} />
                              ) : null}
                              {user.userSubType || 'Not specified'}
                            </p>
                            <p className={styles.location}>
                              <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.locationIcon} />
                              {[user.city, user.state, user.country].filter(Boolean).join(', ') || 'Location not specified'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noResults}>
                    <FontAwesomeIcon icon={faSearch} className={styles.noResultsIcon} />
                    <p>No results found for "{searchTerm}"</p>
                    {Object.keys(searchFilters).length > 0 && (
                      <p className={styles.noResultsSubtext}>
                        Try adjusting your filters or search terms
                      </p>
                    )}
                  </div>
                )
              ) : recentlyViewedUsers.length > 0 ? (
                <div className={styles.recentContainer}>
                  <div className={styles.recentHeader}>
                    <h3>
                      <FontAwesomeIcon icon={faHistory} className={styles.recentIcon} />
                      Recently Viewed
                    </h3>
                    <button 
                      className={styles.clearAllButton}
                      onClick={clearRecentlyViewedUsers}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className={styles.resultsGrid}>
                    {recentlyViewedUsers.map(user => (
                      <div 
                        key={user.userId} 
                        className={styles.userCard}
                      >
                        <button 
                          className={styles.removeRecentButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromRecentlyViewed(user.userId);
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <div 
                          className={styles.userCardContent}
                          onClick={() => handleUserSelect(user)}
                        >
                          <img 
                            src={user.profilePictureUrl || defaultProfilePhoto}
                            alt={user.fullName}
                            className={styles.profilePhoto}
                          />
                          <div className={styles.userInfo}>
                            <div className={styles.nameContainer}>
                              <h3>{user.fullName}</h3>
                              {(user.idVerificationStatus === 'Verified' || (user.isBand && user.verified)) && (
                                <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                              )}
                            </div>
                            <p className={styles.username}>@{user.userName}</p>
                            <p className={styles.subtype}>
                              {user.isBand ? (
                                <FontAwesomeIcon icon={faMusic} className={styles.bandIcon} />
                              ) : null}
                              {user.userSubType || 'Not specified'}
                            </p>
                            <p className={styles.location}>
                              <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.locationIcon} />
                              {[user.city, user.state, user.country].filter(Boolean).join(', ') || 'Location not specified'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.searchPrompt}>
                  <FontAwesomeIcon icon={faSearch} className={styles.promptIcon} />
                  <p>Search for users, bands, or organizations</p>
                  <p className={styles.promptHint}>Enter a name, username, or select a type to start</p>
                </div>
              )
            ) : (
              // Discovery results section
              hasDiscovered ? (
                discoveryResults.length > 0 ? (
                  <>
                    <div className={styles.discoverResultsHeader}>
                      <h3>
                        Available {selectedUserType === 'Band' ? 'Bands' : `${selectedUserType}s`}
                        {formattedDiscoveryDate && (
                          <span className={styles.dateLabel}> on {formattedDiscoveryDate}</span>
                        )}
                      </h3>
                      <div className={styles.resultsCount}>
                        Found {discoveryResults.length} {discoveryResults.length === 1 ? 'result' : 'results'}
                        {hasMoreDiscoveryResults && '+'}
                      </div>
                    </div>
                    
                    <div className={styles.resultsGrid}>
                      {discoveryResults.map(item => (
                        <div 
                          key={item.userId} 
                          className={styles.userCard}
                          onClick={() => handleUserSelect(item)}
                        >
                          <div className={styles.userCardContent}>
                            <img 
                              src={item.profilePictureUrl || defaultProfilePhoto}
                              alt={item.fullName}
                              className={styles.profilePhoto}
                            />
                            <div className={styles.userInfo}>
                              <div className={styles.nameContainer}>
                                <h3>{item.fullName}</h3>
                                {(item.idVerificationStatus === 'Verified' || (item.isBand && item.verified)) && (
                                  <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                                )}
                              </div>
                              <p className={styles.username}>@{item.userName}</p>
                              <p className={styles.subtype}>
                                {item.isBand ? (
                                  <FontAwesomeIcon icon={faMusic} className={styles.bandIcon} />
                                ) : null}
                                {item.userSubType || 'Not specified'}
                              </p>
                              <p className={styles.location}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.locationIcon} />
                                {[item.city, item.state, item.country].filter(Boolean).join(', ') || 'Location not specified'}
                              </p>
                              {discoveryDate && (
                                <div className={styles.availabilityTag}>
                                  <FontAwesomeIcon icon={faCalendarAlt} className={styles.availabilityIcon} />
                                  Available on {formattedDiscoveryDate}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {hasMoreDiscoveryResults && (
                      <div className={styles.loadMoreContainer}>
                        <button 
                          className={styles.loadMoreButton}
                          onClick={() => handleDiscovery(true)}
                          disabled={isLoading || isDiscoveryLimitReached()}
                        >
                          {isLoading ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={styles.noResults}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.noResultsIcon} />
                    <p>No available {selectedUserType === 'Band' ? 'Bands' : `${selectedUserType}s`} found{formattedDiscoveryDate ? ` on ${formattedDiscoveryDate}` : ''}</p>
                    {(selectedSubTypes.length > 0 || selectedCountry) && (
                      <p className={styles.noResultsSubtext}>
                        Try adjusting your filters or selecting a different date
                      </p>
                    )}
                  </div>
                )
              ) : (
                <div className={styles.searchPrompt}>
                  <FontAwesomeIcon icon={faCalendarAlt} className={styles.promptIcon} />
                  <p>Select all filters and a date to find available professionals</p>
                  <p className={styles.promptHint}>All fields are required to start searching</p>
                </div>
              )
            )}
          </div>
        </AuthCheckWrapper>
      </div>
      <RightMenu />

      {/* Filter Modal - only shown for Search tab */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Filter Options</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setIsModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className={styles.filterSection}>
              <label>
                <FontAwesomeIcon icon={faUser} className={styles.filterSectionIcon} />
                Profession/SubType
              </label>
              
              <div className={styles.selectedSubtypes}>
                {selectedSubTypes.map((subType) => (
                  <div key={subType} className={styles.subtypeTag}>
                    <span>{subType}</span>
                    <button onClick={() => removeSelectedSubType(subType)}>×</button>
                  </div>
                ))}
              </div>
              
              <div className={styles.subTypeDropdownContainer} ref={subTypeDropdownRef}>
                <div 
                  className={styles.subTypeDropdownHeader} 
                  onClick={() => setIsSubTypeDropdownOpen(!isSubTypeDropdownOpen)}
                >
                  <span>{selectedSubTypes.length > 0 ? `${selectedSubTypes.length} selected` : 'Select SubType'}</span>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className={`${styles.dropdownIcon} ${isSubTypeDropdownOpen ? styles.open : ''}`} 
                  />
                </div>
                
                {isSubTypeDropdownOpen && (
                  <div className={styles.subTypeDropdownMenu}>
                    <div className={styles.subTypeSearchContainer}>
                      <input
                        type="text"
                        placeholder="Search subtypes..."
                        value={subTypeSearchTerm}
                        onChange={(e) => setSubTypeSearchTerm(e.target.value)}
                        className={styles.subTypeSearchInput}
                      />
                    </div>
                    
                    <div className={styles.subTypeOptionsList}>
                      {filteredSubTypes.map(subType => (
                        <div 
                          key={subType.value} 
                          className={`${styles.subTypeOption} ${selectedSubTypes.includes(subType.label) ? styles.selected : ''}`}
                          onClick={() => handleSubTypeSelection(subType.label)}
                        >
                          <span>{subType.label}</span>
                          {selectedSubTypes.includes(subType.label) && (
                            <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                          )}
                        </div>
                      ))}
                      
                      {filteredSubTypes.length === 0 && (
                        <div className={styles.noSubTypeResults}>
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.filterSection}>
              <label>
                <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.filterSectionIcon} />
                Location
              </label>
              
              <div className={styles.locationFields}>
                <div className={styles.locationField}>
                  <label className={styles.locationLabel}>Country</label>
                  <select
                    value={selectedCountry?.isoCode || ''}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className={styles.locationSelect}
                  >
                    <option value="">Select Country</option>
                    {Country.getAllCountries().map((country) => (
                      <option key={country.isoCode} value={country.isoCode}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.locationField}>
                  <label className={styles.locationLabel}>State/Province</label>
                  <select
                    value={selectedState?.isoCode || ''}
                    onChange={(e) => handleStateChange(e.target.value)}
                    className={styles.locationSelect}
                    disabled={!selectedCountry}
                  >
                    <option value="">Select State</option>
                    {availableStates.map((state) => (
                      <option key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.locationField}>
                  <label className={styles.locationLabel}>City</label>
                  <select
                    value={selectedCity?.name || ''}
                    onChange={(e) => handleCityChange(e.target.value)}
                    className={styles.locationSelect}
                    disabled={!selectedState}
                  >
                    <option value="">Select City</option>
                    {availableCities.map((city) => (
                      <option key={`${city.name}-${city.stateCode}`} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.filterActions}>
              <button 
                className={styles.cancelButton}
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className={styles.applyButton}
                onClick={applyFilters}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Limit Reached Modal */}
      {isLimitModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsLimitModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{limitModalType === 'search' ? 'Search Limit Reached' : 'Discovery Limit Reached'}</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setIsLimitModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className={styles.limitModalContent}>
              <FontAwesomeIcon icon={faLock} className={styles.limitModalIcon} />
              <p>
                You've reached your monthly {limitModalType} limit of {limitModalType === 'search' ? searchLimit : discoveryLimit} 
                for your {currentUser?.membershipTier} membership.
              </p>
              <p>Your limits will reset at the beginning of next month.</p>
              <p>Upgrade your membership to get higher limits.</p>
              
              <div className={styles.limitTable}>
                <div className={styles.limitTableHeader}>
                  <span>Membership</span>
                  <span>Monthly Searches</span>
                  <span>Monthly Discoveries</span>
                </div>
                <div className={`${styles.limitTableRow} ${currentUser?.membershipTier === 'Free' ? styles.currentTier : ''}`}>
                  <span>Free</span>
                  <span>50</span>
                  <span>Not available</span>
                </div>
                <div className={`${styles.limitTableRow} ${currentUser?.membershipTier === 'Basic' ? styles.currentTier : ''}`}>
                  <span>Basic</span>
                  <span>1,000</span>
                  <span>1,000</span>
                </div>
                <div className={`${styles.limitTableRow} ${currentUser?.membershipTier === 'Pro' ? styles.currentTier : ''}`}>
                  <span>Pro</span>
                  <span>2,000</span>
                  <span>2,000</span>
                </div>
                <div className={`${styles.limitTableRow} ${currentUser?.membershipTier === 'Elite' ? styles.currentTier : ''}`}>
                  <span>Elite</span>
                  <span>3,000</span>
                  <span>3,000</span>
                </div>
              </div>
              
              <button 
                className={styles.upgradeModalButton}
                onClick={() => {
                  setIsLimitModalOpen(false);
                  navigate('/membership');
                }}
              >
                <FontAwesomeIcon icon={faRocket} />
                Upgrade Membership
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;